import React, { useEffect, useMemo, useRef, useState } from 'react'
import { usePageTracking } from '../hooks/usePageTracking'

// --- START: Localization Data for Play Page ---
const locales = {
  en: {
    title: 'Rock-Paper-Scissors · Particle Physics Simulation',
    h2: 'Rock-Paper-Scissors · Physics Simulation',
    back: '← Back to Home',
    actions: { start: 'Start', pause: 'Pause', reset: 'Reset' },
    labels: { density: 'Density', speed: 'Speed', size: 'Size' },
    counts: { R: 'Rock', P: 'Paper', S: 'Scissors' },
    note:
      'Tip: Higher density and speed lead to faster faction conversion. All particles have equal mass and use approximate elastic collision.',
  },
  'zh-CN': {
    title: '石头剪刀布 · 粒子物理模拟',
    h2: '石头剪刀布 · 物理碰撞模拟',
    back: '← 回到首页',
    actions: { start: '开始', pause: '暂停', reset: '重置' },
    labels: { density: '数量', speed: '速度', size: '大小' },
    counts: { R: '石头', P: '布', S: '剪刀' },
    note: '提示：数量越多、速度越快，阵营转化会更快发生；所有粒子等质量，使用近似弹性碰撞。',
  },
}
// --- END: Localization Data for Play Page ---

const EMOJI = { R: '🪨', P: '📄', S: '✂️' }
const WINS_OVER = { R: 'S', S: 'P', P: 'R' }

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

function resolveElasticCollision(a, b) {
  const nx = b.x - a.x,
    ny = b.y - a.y
  const dist = Math.hypot(nx, ny) || 1e-6
  const nux = nx / dist,
    nuy = ny / dist

  const vaN = a.vx * nux + a.vy * nuy
  const vbN = b.vx * nux + b.vy * nuy

  // Equal mass, perfectly elastic, swap normal components
  const vaN_after = vbN,
    vbN_after = vaN

  const vaT_x = a.vx - vaN * nux,
    vaT_y = a.vy - vaN * nuy
  const vbT_x = b.vx - vbN * nux,
    vbT_y = b.vy - vbN * nuy

  a.vx = vaT_x + vaN_after * nux
  a.vy = vaT_y + vaN_after * nuy
  b.vx = vbT_x + vbN_after * nux
  b.vy = vbT_y + vbN_after * nuy
}

function applyRPSConversion(a, b) {
  if (WINS_OVER[a.kind] === b.kind) b.kind = a.kind
  else if (WINS_OVER[b.kind] === a.kind) a.kind = b.kind
}

class SpatialHash {
  constructor(cellSize) {
    this.cell = cellSize
    this.map = new Map()
  }
  key(cx, cy) {
    return `${cx},${cy}`
  }
  insert(p) {
    const cx = Math.floor(p.x / this.cell),
      cy = Math.floor(p.y / this.cell)
    const k = this.key(cx, cy)
    const arr = this.map.get(k) || []
    arr.push(p.id)
    this.map.set(k, arr)
  }
  neighbors(x, y) {
    const cx = Math.floor(x / this.cell),
      cy = Math.floor(y / this.cell)
    const out = []
    for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) out.push(this.key(cx + dx, cy + dy))
    return out
  }
}

export default function Play({ lang }) {
  usePageTracking('Play')

  const t = locales[lang] || locales.en

  // Embed mode for portfolio previews: https://.../play?embed=1
  const isEmbed = useMemo(() => {
    if (typeof window === 'undefined') return false
    const sp = new URLSearchParams(window.location.search)
    const v = sp.get('embed')
    return v === '1' || v === 'true'
  }, [])

  return (
    <div className="page">
      {!isEmbed && (
        <header className="hero">
          {/* Keep site branding on the normal page, but remove it in embedded previews to avoid awkward clipping */}
          <h1 className="title">
            <span>social media academy</span>
          </h1>
        </header>
      )}

      <div className="play-wrap" style={isEmbed ? { paddingTop: 12, paddingBottom: 12 } : undefined}>
        <h2 className="play-title">🎮 {t.title}</h2>
        <RPSArena t={t} isEmbed={isEmbed} />

        {!isEmbed && (
          <div className="play-back">
            <a href="/" className="back">
              {t.back}
            </a>
          </div>
        )}
      </div>

      {!isEmbed && (
        <footer className="footer">
          <span>&copy; {new Date().getFullYear()} social media academy</span>
        </footer>
      )}
    </div>
  )
}

function RPSArena({ t, isEmbed }) {
  const canvasRef = useRef(null)

  const [running, setRunning] = useState(true)
  const [density, setDensity] = useState(60)
  const [speed, setSpeed] = useState(1.0)
  const [radius, setRadius] = useState(14)
  const [counts, setCounts] = useState({ R: 0, P: 0, S: 0 })

  const pixelRatio = useMemo(() => (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1), [])

  const particlesRef = useRef([])
  const rafRef = useRef(null)
  const lastRef = useRef(null)
  const sizeRef = useRef({ w: 0, h: 0 })
  const collidingPairs = useRef(new Set())
  const countsCooldownRef = useRef(0)

  const makeParticles = useMemo(() => {
    return (W, H) => {
      const arr = []
      const n = density

      const third = Math.max(1, Math.floor(n / 3))
      const rest = n - third * 2
      const kinds = [...Array(third).fill('R'), ...Array(third).fill('P'), ...Array(rest).fill('S')]

      let id = 0
      for (const k of kinds) {
        const r = radius
        const x = clamp(Math.random() * W, r + 1, W - r - 1)
        const y = clamp(Math.random() * H, r + 1, H - r - 1)
        const theta = Math.random() * Math.PI * 2
        const v = (0.6 + Math.random() * 0.8) * 100
        arr.push({ id: id++, kind: k, x, y, vx: Math.cos(theta) * v, vy: Math.sin(theta) * v, r })
      }
      return arr
    }
  }, [density, radius])

  // Keep the canvas simulation in CSS pixels, and scale the drawing context for retina displays.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const parent = canvas.parentElement
    if (!parent) return

    const baseHeight = isEmbed ? 380 : 560

    function resize() {
      const rect = parent.getBoundingClientRect()

      // Target a height that (a) fits portfolio previews, and (b) remains responsive.
      // Use a mild aspect hint (16/10) so it "feels" right inside your preview cards.
      const aspectHintH = Math.round((rect.width * 10) / 16)
      const cssW = Math.max(240, Math.floor(rect.width))
      const cssH = clamp(aspectHintH, isEmbed ? 280 : 360, baseHeight)

      sizeRef.current = { w: cssW, h: cssH }

      canvas.style.width = '100%'
      canvas.style.height = `${cssH}px`

      canvas.width = Math.floor(cssW * pixelRatio)
      canvas.height = Math.floor(cssH * pixelRatio)

      // If nothing is initialized yet, seed particles once we have a size.
      if (!particlesRef.current.length) {
        particlesRef.current = makeParticles(cssW, cssH)
        setCounts(countKinds(particlesRef.current))
      } else {
        // Keep existing particles within the new bounds to avoid "teleporting" visuals.
        for (const p of particlesRef.current) {
          p.x = clamp(p.x, p.r, cssW - p.r)
          p.y = clamp(p.y, p.r, cssH - p.r)
        }
      }
    }

    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(parent)
    return () => ro.disconnect()
  }, [isEmbed, makeParticles, pixelRatio])

  const reset = () => {
    const { w, h } = sizeRef.current
    if (!w || !h) return
    particlesRef.current = makeParticles(w, h)
    setCounts(countKinds(particlesRef.current))
  }

  useEffect(() => {
    reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeParticles])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const step = (ts) => {
      if (lastRef.current == null) lastRef.current = ts
      const dt = Math.min(0.035, (ts - lastRef.current) / 1000)
      lastRef.current = ts

      if (running) simulate(dt)
      render()

      rafRef.current = requestAnimationFrame(step)
    }

    const simulate = (dt) => {
      const { w: W, h: H } = sizeRef.current
      const ps = particlesRef.current

      for (const p of ps) {
        p.x += p.vx * dt * speed
        p.y += p.vy * dt * speed

        if (p.x < p.r) {
          p.x = p.r
          p.vx = Math.abs(p.vx)
        } else if (p.x > W - p.r) {
          p.x = W - p.r
          p.vx = -Math.abs(p.vx)
        }

        if (p.y < p.r) {
          p.y = p.r
          p.vy = Math.abs(p.vy)
        } else if (p.y > H - p.r) {
          p.y = H - p.r
          p.vy = -Math.abs(p.vy)
        }
      }

      // Spatial hash in CSS pixels
      const cell = Math.max(16, Math.floor(radius * 2.2))
      const grid = new SpatialHash(cell)
      grid.map.clear()
      for (const p of ps) grid.insert(p)

      collidingPairs.current.clear()

      for (const a of ps) {
        const keys = grid.neighbors(a.x, a.y)
        for (const k of keys) {
          const idxs = grid.map.get(k)
          if (!idxs) continue
          for (const id of idxs) {
            if (id <= a.id) continue
            const b = ps[id]

            const dx = b.x - a.x,
              dy = b.y - a.y
            const dist = Math.hypot(dx, dy)
            const minD = a.r + b.r

            if (dist > 0 && dist < minD) {
              const pairKey = `${a.id}-${b.id}`
              if (!collidingPairs.current.has(pairKey)) {
                collidingPairs.current.add(pairKey)

                // Separate overlap
                const overlap = (minD - dist) / 2
                const ux = dx / (dist || 1e-6),
                  uy = dy / (dist || 1e-6)
                a.x -= ux * overlap
                a.y -= uy * overlap
                b.x += ux * overlap
                b.y += uy * overlap

                resolveElasticCollision(a, b)
                applyRPSConversion(a, b)
              }
            }
          }
        }
      }

      // Avoid setState 60 times/sec (can look "janky" in iframes). Update ~8 times/sec.
      countsCooldownRef.current += dt
      if (countsCooldownRef.current >= 0.12) {
        countsCooldownRef.current = 0
        setCounts(countKinds(ps))
      }
    }

    const render = () => {
      const { w: W, h: H } = sizeRef.current

      // Clear in device pixels
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw in CSS pixels, scaled for retina
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.font = `${Math.floor(radius * 1.8)}px system-ui, Apple Color Emoji, Segoe UI Emoji`

      for (const p of particlesRef.current) {
        // subtle shadow
        ctx.beginPath()
        ctx.arc(p.x, p.y + 2, p.r * 0.9, 0, Math.PI * 2)
        ctx.globalAlpha = 0.12
        ctx.fillStyle = 'black'
        ctx.fill()
        ctx.globalAlpha = 1

        ctx.fillText(EMOJI[p.kind], p.x, p.y + (EMOJI[p.kind] === '✂️' ? -2 : 0))
      }

      // Keep transform clean for any future draws
      ctx.setTransform(1, 0, 0, 1, 0, 0)
    }

    rafRef.current = requestAnimationFrame(step)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastRef.current = null
    }
  }, [running, speed, radius, pixelRatio])

  return (
    <div className="arena">
      <div className="arena-row">
        <div className="arena-title">{t.h2}</div>
        <div className="arena-actions">
          <button className="btn" onClick={() => setRunning((v) => !v)}>
            {running ? t.actions.pause : t.actions.start}
          </button>
          <button className="btn" onClick={reset}>
            {t.actions.reset}
          </button>
        </div>
      </div>

      <div className="arena-grid">
        <label className="field">
          <span>{t.labels.density}</span>
          <input type="range" min="10" max="200" value={density} onChange={(e) => setDensity(+e.target.value)} />
          <em>{density}</em>
        </label>
        <label className="field">
          <span>{t.labels.speed}</span>
          <input type="range" min="0.2" max="2.0" step="0.1" value={speed} onChange={(e) => setSpeed(+e.target.value)} />
          <em>{speed.toFixed(1)}×</em>
        </label>
        <label className="field">
          <span>{t.labels.size}</span>
          <input type="range" min="8" max="24" value={radius} onChange={(e) => setRadius(+e.target.value)} />
          <em>{radius}</em>
        </label>
      </div>

      <div className="arena-badges">
        <span>🪨 {t.counts.R}：{counts.R}</span>
        <span>📄 {t.counts.P}：{counts.P}</span>
        <span>✂️ {t.counts.S}：{counts.S}</span>
      </div>

      <div className="arena-canvas">
        <canvas ref={canvasRef} />
      </div>

      {/* In embed mode, keep the page compact; the tip text can be long and causes visual clipping in preview cards. */}
      {!isEmbed && <p className="arena-note">{t.note}</p>}
    </div>
  )
}

function countKinds(ps) {
  let cR = 0,
    cP = 0,
    cS = 0
  for (const p of ps) {
    if (p.kind === 'R') cR++
    else if (p.kind === 'P') cP++
    else cS++
  }
  return { R: cR, P: cP, S: cS }
}
